import React, { useState } from 'react';
import { MapPin, Search, Check, X, Globe } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useCurrency } from '../../context/CurrencyContext';
import { countryFlag } from '../../data/countries';

const CountrySelector = () => {
    const { 
        showCountrySelector, 
        setShowCountrySelector, 
        supportedCountries, 
        changeCountry,
        detectedCountry,
        detectedCountryName,
        detectionSource
    } = useCurrency();
    
    const [searchQuery, setSearchQuery] = useState('');

    if (!showCountrySelector) return null;

    const filteredCountries = supportedCountries.filter(country => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return true;
        return country.name.toLowerCase().includes(q) ||
            country.code.toLowerCase().includes(q) ||
            country.currency.toLowerCase().includes(q);
    });

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div 
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={() => setShowCountrySelector(false)}
            />
            
            {/* Modal */}
            <div className="relative bg-background rounded-xl shadow-2xl max-w-md w-full max-h-[80vh] flex flex-col animate-in fade-in zoom-in duration-300">
                {/* Header */}
                <div className="p-4 border-b">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <Globe className="w-5 h-5 text-primary" />
                            <h2 className="text-lg font-semibold">Select Your Country</h2>
                        </div>
                        <button 
                            onClick={() => setShowCountrySelector(false)}
                            className="text-muted-foreground hover:text-foreground transition-colors"
                            aria-label="Close"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    
                    {/* Current detection info */}
                    {detectedCountry && (
                        <p className="text-sm text-muted-foreground mb-3">
                            Currently detected: <span className="font-medium">{detectedCountryName}</span>
                            <span className="text-xs ml-1">({detectionSource})</span>
                        </p>
                    )}
                    
                    {/* Search */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder="Search countries..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10"
                            data-testid="country-search-input"
                        />
                    </div>
                </div>

                {/* Country List */}
                <div className="flex-1 overflow-y-auto p-2">
                    <div className="space-y-1">
                        {filteredCountries.map((country) => (
                            <button
                                key={country.code}
                                onClick={() => changeCountry(country.code)}
                                className={`w-full flex items-center justify-between p-3 rounded-lg hover:bg-accent transition-colors text-left ${
                                    detectedCountry === country.code ? 'bg-primary/10' : ''
                                }`}
                                data-testid={`country-option-${country.code}`}
                            >
                                <div className="flex items-center gap-3">
                                    <span className="text-2xl">
                                        {country.flag || countryFlag(country.code) || '🌍'}
                                    </span>
                                    <div>
                                        <p className="font-medium">{country.name}</p>
                                        <p className="text-sm text-muted-foreground">
                                            Currency: {country.currency}
                                        </p>
                                    </div>
                                </div>
                                {detectedCountry === country.code && (
                                    <Check className="w-5 h-5 text-primary" />
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t">
                    <p className="text-xs text-muted-foreground text-center">
                        Your selection will update currency and show relevant sellers.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default CountrySelector;
